/* eslint-disable no-confusing-arrow */
import breakpoints from 'base/constants/breakpoints';
import styled from 'styled-components';
import { Button } from 'syngenta-digital-cropwise-react-ui-kit';

export const ReportOverviewWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  padding: 24px 64px;
`;

export const ReportOverviewSection = styled.div<{
  isAISummarySupported: boolean;
  isCollapsed: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${({ isCollapsed }) => (isCollapsed ? '20px 20px 0 20px' : '20px')};
  margin-top: 15px;
  margin-bottom: 40px;
  gap: 16px;
  border-radius: 16px;
  background: #232630;
  backdrop-filter: blur(5px);
  color: #fff;

  ${({ isAISummarySupported }) =>
    isAISummarySupported
      ? `
        border: none;
        &::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 16px;
          padding: 2px;
          background: linear-gradient(90.22deg, #1f87dc -17.61%, #9154e9 26.22%, #9154e9 70.06%, #3bb4a6 115.22%);
          -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }
      `
      : `
        border: 2px solid var(--Neutral-neutral-30, #c2c7d0);
      `}
`;

export const IconBox = styled.span`
  border-radius: 6px;
  background: rgba(105, 111, 136, 0.5);
  display: flex;
  width: 24px;
  height: 24px;
  padding: 6.75px 4.836px 4.25px 4.164px;
  justify-content: center;
  align-items: center;
  margin-right: 12px;

  .icon-size {
    width: 16px;
    height: 16px;
  }
`;

export const StyledHeaderText = styled.span`
  font-family: 'Noto Sans';
  font-size: 30px;
  font-style: normal;
  font-weight: 600;
  line-height: 38px;
  letter-spacing: -0.375px;
  text-align: left;
`;

export const StyledHeaderSmallText = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-family: 'Noto Sans';
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: -0.15px;
  text-align: left;
`;

export const StyledOsrSummaryText = styled.span<{ isCollapsed?: boolean }>`
  font-family: Noto Sans;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: -0.12px;
  text-align: left;
  white-space: pre-line;
  ${(props) =>
    props.isCollapsed
      ? `
      display: block;
      max-height: calc(24px * 3.6);
      overflow: hidden;

      @media ${breakpoints.maxWidth.md} {
        max-height: calc(24px * 5.4);
      }
    `
      : ''}

  &::after {
    content: ' ';
  }
`;
export const StyledSummaryHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

export const StyledSummaryGeneratedText = styled.div`
  display: flex;
  font-family: Noto Sans;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  gap: 10px;
  letter-spacing: -0.75%;
  vertical-align: middle;
  background: linear-gradient(
    90.22deg,
    #31b4f2 -17.61%,
    #d2bdf9 26.22%,
    #d2bdf9 70.06%,
    #46bbb0 115.22%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  span {
    font-weight: 600;
  }
`;

export const StyledAISummaryWarningText = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.75%;
  vertical-align: middle;
  color: #a3a9b9;
  text-align: left;
  font-style: italic;

  align-items: center;
  gap: 16px;
`;

export const StyledAISummaryWarningTextUnderlined = styled.span<{ isOpen?: boolean }>`
  text-decoration: underline;
  font-style: normal;
  cursor: pointer;
  color: ${({ isOpen }) => (isOpen ? '#8b4ae6' : 'inherit')};
  transition: color 0.3s ease;
`;

export const StyledSummaryFeedbackContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media screen and (max-width: ${breakpoints.maxWidth.sm}) {
    gap: 6px;
  }
`;

export const StyledFeedbackContentLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media screen and (max-width: ${breakpoints.maxWidth.sm}) {
    gap: 6px;
  }
`;

export const StyledSummaryFeedbackText = styled.div`
  font-family: 'Noto Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.12px;
  color: #a3a9b9;
`;

export const StyledFeedbackButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent !important;
  padding: 8px;
  border: none !important;

  &:hover {
    background: transparent !important;
    border: none !important;
    transform: scale(1.15) translateY(-1px);
  }
`;

export const StyledIcon = styled.span`
  display: flex;
  justify-content: right;
  gap: 8px;

  button {
    transition: all 0.2s ease;
  }
`;

export const StyledSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
`;

export const StyledSkeletonLine = styled.div`
  height: 15px;
  border-radius: 8px;
  background: linear-gradient(
    90.22deg,
    #eaf6ff -17.61%,
    #e5d7fa 26.22%,
    #e5d7fa 70.06%,
    #dbf9f6 115.22%
  );
  background-size: 1000px 100%;
  animation: shimmer 2s infinite;

  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  &:nth-child(3) {
    width: 80%;
  }
`;

export const StyledCOllapseButton = styled.button`
  background: none;
  border: none;
`;
